import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';
import { useTranslation } from '@/lib/i18n';
import { cn } from '@/lib/utils';

// ── SVG Flag Components ───────────────────────────────────────────────────────

const FlagDE = ({ className }) => (
  <svg className={className} viewBox="0 0 20 15" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <rect width="20" height="5" fill="#000000" />
    <rect y="5" width="20" height="5" fill="#DD0000" />
    <rect y="10" width="20" height="5" fill="#FFCE00" />
  </svg>
);

const FlagGB = ({ className }) => (
  <svg className={className} viewBox="0 0 20 15" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    {/* Blue background */}
    <rect width="20" height="15" fill="#012169" />
    {/* White saltire (diagonal cross) */}
    <path d="M0,0 L20,15 M20,0 L0,15" stroke="white" strokeWidth="3" />
    {/* Red saltire (narrower, over white) */}
    <path d="M0,0 L20,15 M20,0 L0,15" stroke="#C8102E" strokeWidth="1.5" />
    {/* White horizontal & vertical cross */}
    <rect x="0" y="5.5" width="20" height="4" fill="white" />
    <rect x="8" y="0" width="4" height="15" fill="white" />
    {/* Red horizontal & vertical cross (on top) */}
    <rect x="0" y="6.25" width="20" height="2.5" fill="#C8102E" />
    <rect x="8.75" y="0" width="2.5" height="15" fill="#C8102E" />
  </svg>
);

// ── Language config ────────────────────────────────────────────────────────────

const LANGUAGES = [
  { code: 'de', label: 'Deutsch', short: 'DE', Flag: FlagDE },
  { code: 'en', label: 'English', short: 'EN', Flag: FlagGB },
];

// ── Component ─────────────────────────────────────────────────────────────────

export function LanguageSwitcher() {
  const { lang, setLang } = useLanguage();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const show = process.env.REACT_APP_SHOW_LANGUAGE_SWITCHER !== 'false';

  // Close on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, []);

  if (!show) return null;

  const current = LANGUAGES.find((l) => l.code === lang) ?? LANGUAGES[0];

  const handleSelect = (code) => {
    setLang(code);
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative" data-testid="language-switcher">
      {/* Trigger button */}
      <button
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'flex items-center gap-1.5 px-2 py-1.5 rounded-md text-sm font-medium transition-colors',
          'text-muted-foreground hover:text-foreground hover:bg-black/5 dark:hover:bg-white/5',
          open && 'bg-black/5 dark:bg-white/5 text-foreground'
        )}
        aria-label={t('a11y.languageSelector')}
        aria-expanded={open}
        aria-haspopup="listbox"
        data-testid="language-switcher-trigger"
      >
        <current.Flag className="w-5 h-4 rounded-[2px] shrink-0" />
        <span className="text-xs font-semibold tracking-wide">{current.short}</span>
        <ChevronDown
          className={cn('w-3 h-3 transition-transform duration-150', open && 'rotate-180')}
        />
      </button>

      {/* Dropdown */}
      <AnimatePresence>
        {open && (
          <motion.ul
            role="listbox"
            aria-label={t('a11y.languageSelector')}
            initial={{ opacity: 0, y: -6, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.97 }}
            transition={{ duration: 0.12 }}
            className={cn(
              'absolute right-0 top-full mt-1 z-50',
              'min-w-[130px] py-1 rounded-xl shadow-xl',
              'bg-card border border-border',
              'overflow-hidden'
            )}
          >
            {LANGUAGES.map(({ code, label, short, Flag }) => (
              <li key={code} role="option" aria-selected={lang === code}>
                <button
                  onClick={() => handleSelect(code)}
                  className={cn(
                    'w-full flex items-center gap-2.5 px-3 py-2 text-sm transition-colors text-left',
                    lang === code
                      ? 'bg-primary/10 text-primary font-semibold'
                      : 'text-foreground hover:bg-black/5 dark:hover:bg-white/5'
                  )}
                  data-testid={`language-option-${code}`}
                >
                  <Flag className="w-5 h-4 rounded-[2px] shrink-0" />
                  <span className="font-mono text-xs font-bold tracking-wide opacity-60 shrink-0 w-5">
                    {short}
                  </span>
                  <span>{label}</span>
                </button>
              </li>
            ))}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
}
